import { Injectable, inject } from '@angular/core';
import { FileStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { IBizEntity, LoggerService } from '@optima-stride-ai/core';

/**
 * Service for fetching file URLs from the FileStateStore API
 * 
 * This service provides a centralized way to fetch file metadata including:
 * - File URLs (fileUrl)
 * - Thumbnail URLs (thumbUrl)
 * 
 * It follows SOLID principles with single responsibility for file URL management
 */
@Injectable({
  providedIn: 'root'
})
export class EntityFileUrlService {
  private readonly fileStateStoreService = inject(FileStateStoreService);
  /**
   * Fetch file URL and thumbnail URL for a given business entity
   * OPTIMIZED for instant thumbnail display - minimal processing, maximum speed
   * 
   * @param bizEntityId - The ID of the business entity
   * @returns Promise with file metadata including fileUrl and thumbUrl, or null if error
   * 
   * Response format: { name: string, fileUrl: string[], thumbUrl: string, ... }
   */
  async getFileUrl(bizEntityId: string | number): Promise<{
    name: string;
    fileUrl: string[];
    thumbUrl: string;
  } | null> {
    // Fast validation - no logging
    if (!bizEntityId) {
      return null;
    }

    try {
      // Direct API call - no intermediate processing
      const response = await this.fileStateStoreService.getState([bizEntityId.toString()]);
      
      // Fast extraction - single operation
      const fileMetadata = Array.isArray(response) ? response[0] : response;
      
      if (!fileMetadata) {
        return null;
      }

      // Return immediately with minimal object creation
      return {
        name: fileMetadata.name || '',
        fileUrl: fileMetadata.fileUrl || [],
        thumbUrl: fileMetadata.thumbUrl || ''
      };
    } catch (error) {
      // Silent error handling for performance - log only in development
      return null;
    }
  }

  /**
   * Fetch file URL for a business entity object
   * 
   * @param entity - The business entity
   * @returns Promise with file metadata including fileUrl and thumbUrl, or null if error
   */
  async getFileUrlForEntity(entity: IBizEntity): Promise<{
    name: string;
    fileUrl: string[];
    thumbUrl: string;
  } | null> {
    return this.getFileUrl(entity.id);
  }

  /**
   * Fetch thumbnail URL only for a given business entity
   * 
   * @param bizEntityId - The ID of the business entity
   * @returns Promise with thumbnail URL string, or null if error
   */
  async getThumbnailUrl(bizEntityId: string | number): Promise<string | null> {
    const fileMetadata = await this.getFileUrl(bizEntityId);
    return fileMetadata?.thumbUrl || null;
  }

  /**
   * Fetch thumbnail URL for a business entity object
   * 
   * @param entity - The business entity
   * @returns Promise with thumbnail URL string, or null if error
   */
  async getThumbnailUrlForEntity(entity: IBizEntity): Promise<string | null> {
    return this.getThumbnailUrl(entity.id);
  }
}
